import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowRight, FiImage } from "react-icons/fi";
import { galleryAPI } from "../../services/api";
import { resolveImageUrl } from "../../utils/helpers";
import { useDataRefresh } from "../../hooks/useDataRefresh";
import useScrollAnimation from "../../hooks/useScrollAnimation";

const fallbackImages = [
  { _id: "1", title: "Annual Sports Day",  category: "Sports"   },
  { _id: "2", title: "Science Exhibition", category: "Academic" },
  { _id: "3", title: "Cultural Program",   category: "Events"   },
  { _id: "4", title: "Graduation Ceremony",category: "Events"   },
  { _id: "5", title: "Campus Tour",        category: "Campus"   },
  { _id: "6", title: "Library Session",    category: "Academic" },
];

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

export default function GalleryPreview() {
  const [imgErrors, setImgErrors] = useState({});
  const { ref, isVisible } = useScrollAnimation();

  const { data: images = [] } = useDataRefresh(
    async () => {
      try {
        const res = await galleryAPI.getAll({ limit: 1000, sortBy: "createdAt", sortOrder: "desc" });
        const allImages = res.data?.data?.images || [];
        if (allImages.length === 0) return fallbackImages;

        const categories = ["Campus", "Events", "Sports", "Academic", "Cultural", "Other"];
        const selectedImages = [];
        const usedCategories = new Set();

        for (const image of allImages) {
          if (usedCategories.size === categories.length) break;
          if (categories.includes(image.category) && !usedCategories.has(image.category)) {
            selectedImages.push(image);
            usedCategories.add(image.category);
          }
        }
        if (selectedImages.length < 6) {
          for (const image of allImages) {
            if (selectedImages.length >= 6) break;
            if (!selectedImages.includes(image)) selectedImages.push(image);
          }
        }
        return selectedImages.slice(0, 6);
      } catch {
        return fallbackImages;
      }
    },
    "gallery",
    { pollInterval: 0 },
  );

  return (
    <section className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">

        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12 gap-8"
        >
          <div>
            <SectionLabel text="Our Memories" />
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0F172A] leading-tight">
              School <span className="text-[#2563EB]">Gallery</span>
            </h2>
          </div>
          <Link
            to="/gallery"
            className="inline-flex items-center gap-3 px-6 py-3 bg-[#2563EB] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#1D4ED8] transition-all rounded-xl shadow-sm hover:shadow-md shrink-0"
          >
            View Full Gallery <FiArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((image, idx) => (
            <motion.div
              key={image._id || idx}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={isVisible ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className="relative overflow-hidden rounded-2xl group h-48 sm:h-64 md:h-72 border border-blue-100 shadow-sm hover:shadow-lg transition-shadow duration-300"
            >
              {image.imageUrl && !imgErrors[image._id] ? (
                <img
                  src={resolveImageUrl(image.imageUrl)}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  onError={() => setImgErrors((prev) => ({ ...prev, [image._id]: true }))}
                />
              ) : (
                <div className="w-full h-full bg-[#F0F4FF] flex items-center justify-center">
                  <FiImage className="w-12 h-12 text-blue-200" />
                </div>
              )}

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-[#1e3a8a]/85 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center p-6 text-center rounded-2xl">
                <p className="text-white font-bold text-lg mb-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  {image.title}
                </p>
                {image.category && (
                  <span className="text-sky-300 text-[10px] font-bold uppercase tracking-widest transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-75">
                    {image.category}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
